import type ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';

import {ReviewableQuery} from './__generated/ReviewableQuery';
import {getCycleScopedStatus, isPublishApprovedForScopedStatus} from './reviewBadgeShared';

type ReviewableGraphQL = ExcludeInaccessible<ReviewableQuery['reviewable']>;

export interface CyclicalReviewPublishState {
  loading: boolean;
  error: boolean;
  inaccessible: boolean;
  reviewRequired: boolean;
  isPublishBlockedByReview: boolean;
  isPublishOptionDisabled: boolean;
}

export interface DeriveCyclicalPublishStateInput {
  openVersionApiId?: string;
  isLatest: boolean;
  isPublishOptionDisabledFromParent?: boolean;
  queryState: 'pending' | 'error' | 'inaccessible' | 'ready';
  reviewable?: ReviewableGraphQL;
}

export function deriveCyclicalPublishState({
  openVersionApiId,
  isLatest,
  isPublishOptionDisabledFromParent = false,
  queryState,
  reviewable,
}: DeriveCyclicalPublishStateInput): CyclicalReviewPublishState {
  const parentPublishDisabled = Boolean(isPublishOptionDisabledFromParent);
  const hasResolvableVersionId = openVersionApiId != null;
  const withoutReviewData = {
    reviewRequired: false,
    isPublishBlockedByReview: false,
  } satisfies Pick<CyclicalReviewPublishState, 'reviewRequired' | 'isPublishBlockedByReview'>;

  if (queryState === 'pending' || queryState === 'error') {
    return {
      ...withoutReviewData,
      loading: queryState === 'pending',
      error: queryState === 'error',
      inaccessible: false,
      isPublishOptionDisabled: parentPublishDisabled || isLatest,
    };
  }

  if (queryState === 'inaccessible') {
    return {
      ...withoutReviewData,
      loading: false,
      error: false,
      inaccessible: true,
      isPublishOptionDisabled: parentPublishDisabled,
    };
  }

  const reviewRequired = (reviewable?.parentReviewLifecycles.length ?? 0) > 0;
  const reviewChanges = reviewable?.review?.reviewChanges ?? [];
  const scopedStatus =
    isLatest && hasResolvableVersionId ? getCycleScopedStatus(reviewChanges, openVersionApiId) : null;
  const isPublishBlockedByReview =
    reviewRequired &&
    isLatest &&
    (!hasResolvableVersionId || !isPublishApprovedForScopedStatus(scopedStatus));

  return {
    loading: false,
    error: false,
    inaccessible: false,
    reviewRequired,
    isPublishBlockedByReview,
    isPublishOptionDisabled: parentPublishDisabled || isPublishBlockedByReview || !isLatest,
  };
}
